import { format } from 'date-fns'
import {
  AreaChart,
  BarChart3,
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  ChevronLeft,
  TrendingUp,
} from 'lucide-react'
import * as React from 'react'

import { cn, parseCalendarDate } from '@/utils'
import { Calendar } from '../Calendar'
import { Button } from '../clickable/Button'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'

interface ChartFiltersProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

const ChartFilters = React.forwardRef<HTMLDivElement, ChartFiltersProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-wrap items-center gap-2', className)}
        {...props}
      >
        {children}
      </div>
    )
  },
)
ChartFilters.displayName = 'ChartFilters'

interface DateRange {
  from?: Date
  to?: Date
}

interface ChartDateRangeFilterProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value?: string | DateRange | null
  onValueChange?: (value: string | DateRange) => void
  initialDate?: string
  finalDate?: string
  presets?: { label: string; value: string }[]
}

const ChartDateRangeFilter = React.forwardRef<
  HTMLDivElement,
  ChartDateRangeFilterProps
>(
  (
    {
      className,
      value,
      onValueChange,
      initialDate,
      finalDate,
      presets = [
        { label: '30d', value: '30d' },
        { label: '60d', value: '60d' },
        { label: '90d', value: '90d' },
        { label: 'Year', value: '1y' },
        { label: 'All', value: 'all' },
      ],
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const [view, setView] = React.useState('menu')

    const [date, setDate] = React.useState(
      typeof value === 'object' && value !== null ? value : null,
    )

    React.useEffect(() => {
      if (typeof value === 'object' && value !== null) {
        setDate(value)
      }
    }, [value])

    const defaultMonth = React.useMemo(() => {
      return finalDate ? parseCalendarDate(finalDate) : new Date()
    }, [finalDate])

    const disabledDates = React.useMemo(() => {
      const disabled: { before?: Date; after?: Date } = {}
      if (initialDate) disabled.before = parseCalendarDate(initialDate)
      if (finalDate) disabled.after = parseCalendarDate(finalDate)
      return disabled
    }, [initialDate, finalDate])

    React.useEffect(() => {
      if (!isOpen) {
        setView('menu')
      }
    }, [isOpen])

    const getLabel = () => {
      if (typeof value === 'object' && value?.from) {
        if (value.to) {
          return `${format(value.from, 'MMM dd')} - ${format(value.to, 'MMM dd')}`
        }
        return format(value.from, 'MMM dd')
      }
      return presets.find((p) => p.value === value)?.label || 'Select range'
    }

    return (
      <div ref={ref} className={className} {...props}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              theme="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs font-medium"
            >
              <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
              {getLabel()}
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0" alignOffset={0}>
            {view === 'menu' && (
              <div className="flex flex-col p-1 min-w-[140px]">
                {presets.map((preset) => {
                  const isSelected = value === preset.value
                  return (
                    <button
                      type="button"
                      key={preset.value}
                      className={cn(
                        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
                        isSelected && 'bg-accent text-accent-foreground',
                      )}
                      onClick={() => {
                        onValueChange?.(preset.value)
                        setIsOpen(false)
                      }}
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        {isSelected && <Check className="h-4 w-4" />}
                      </span>
                      <span className="pl-8">{preset.label}</span>
                    </button>
                  )
                })}
                <div className="h-px bg-muted my-1" />
                <button
                  type="button"
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground pl-8"
                  onClick={() => setView('calendar')}
                >
                  Custom Range
                </button>
              </div>
            )}
            {view === 'calendar' && (
              <div>
                <div className="flex items-center border-b p-2">
                  <Button
                    theme="ghost"
                    size="xs"
                    isIcon
                    onClick={() => setView('menu')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="ml-2 text-sm font-semibold">
                    Select Dates
                  </span>
                </div>
                <Calendar
                  mode="range"
                  defaultMonth={defaultMonth}
                  selected={date || undefined}
                  onSelect={(newDate) => {
                    if (!newDate) {
                      setDate(null)
                    } else if (
                      typeof newDate === 'object' &&
                      'from' in newDate
                    ) {
                      setDate(newDate)
                      if (newDate.from && newDate.to) {
                        onValueChange?.(newDate as { from: Date; to: Date })
                      }
                    }
                  }}
                  initialFocus
                  numberOfMonths={2}
                  disabled={disabledDates}
                />
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    )
  },
)
ChartDateRangeFilter.displayName = 'ChartDateRangeFilter'

const CHART_TYPE_OPTIONS = [
  { value: 'bar', label: 'Bar', icon: BarChart3 },
  { value: 'line', label: 'Line', icon: TrendingUp },
  { value: 'area', label: 'Area', icon: AreaChart },
]

interface ChartTypeOption {
  value: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

interface ChartTypeSwitcherProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string
  onValueChange?: (value: string) => void
  options?: ChartTypeOption[]
}

const ChartTypeSwitcher = React.forwardRef<
  HTMLDivElement,
  ChartTypeSwitcherProps
>(
  (
    { className, value, onValueChange, options = CHART_TYPE_OPTIONS, ...props },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false)

    const selectedOption =
      options.find((opt) => opt.value === value) || options[0]

    return (
      <div ref={ref} className={className} {...props}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              theme="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs font-normal"
            >
              Chart Type: {selectedOption.label}
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0" alignOffset={0}>
            <div className="flex flex-col p-1 min-w-[140px]">
              {options.map((opt) => {
                const OptionIcon = opt.icon
                const isSelected = value === opt.value
                return (
                  <button
                    type="button"
                    key={opt.value}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
                      isSelected && 'bg-accent text-accent-foreground',
                    )}
                    onClick={() => {
                      onValueChange?.(opt.value)
                      setIsOpen(false)
                    }}
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    <OptionIcon className="ml-8 mr-2 h-4 w-4" />
                    <span>{opt.label}</span>
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  },
)
ChartTypeSwitcher.displayName = 'ChartTypeSwitcher'

export {
  ChartFilters,
  ChartDateRangeFilter,
  ChartTypeSwitcher,
  CHART_TYPE_OPTIONS,
}
